// The legacy Class() factory: dynamic metaprogramming that stamps custom
// identity/inheritance props onto Function objects and assigns prototype methods
// by Function.name. The shapes here are genuinely dynamic, so the augmented
// constructor is modeled by a single ClassFunc interface with index signatures
// rather than scattered casts.
interface ClassFunc {
  (...args: unknown[]): unknown;
  new (...args: unknown[]): unknown;
  prototype: Record<string | symbol, unknown> & { __prototypeid__?: number };
  __parent__?: ClassFunc | undefined;
  __statics__?: unknown[];
  _is_static_method?: boolean;
  [key: string]: unknown;
  [key: symbol]: unknown;
}

// Marker wrappers produced by Class.getter/setter/static and consumed by Class().
class ClassGetter {
  func: Function;
  constructor(func: Function) {
    this.func = func;
  }
}
class ClassSetter {
  func: Function;
  constructor(func: Function) {
    this.func = func;
  }
}
class StaticMethod {
  func: Function;
  constructor(func: Function) {
    this.func = func;
  }
}

let prototype_idgen = 1;
const defined_classes: Function[] = [];

const handle_statics = function (cls: ClassFunc, parent: ClassFunc | undefined) {
  for (const k in cls.prototype) {
    if (cls.prototype[k] instanceof StaticMethod) {
      const func = cls.prototype[k] as StaticMethod;

      delete cls.prototype[k];
      cls[k] = func.func;
    }
  }

  if (parent != undefined) {
    for (const k in parent) {
      const v = parent[k];

      //only inherit static methods added to parent with this module
      if (
        (v == undefined || (typeof v === "function" && "_is_static_method" in v)) &&
        !(k in cls)
      ) {
        cls[k] = v;
      }
    }
  }
};
const init_prototype = function init_prototype(cls: ClassFunc, proto: Record<string, unknown>) {
  for (const k in proto) {
    cls.prototype[k] = proto[k];
  }

  cls.prototype.__prototypeid__ = prototype_idgen++;
  cls[Symbol.keystr] = function (this: ClassFunc) {
    return this.prototype.__prototypeid__;
  };

  cls.__parent__ = undefined;
  cls.__statics__ = [];

  handle_statics(cls, undefined);

  return cls.prototype;
};
const inherit = function inherit(
  cls: ClassFunc,
  parent: ClassFunc,
  proto: Record<string, unknown>
) {
  cls.prototype = Object.create(parent.prototype);
  init_prototype(cls, proto);

  return cls.prototype;
};

interface ClassFactory {
  (methods: unknown[]): ClassFunc;
  getter(func: Function): ClassGetter;
  get(func: Function): ClassGetter;
  setter(func: Function): ClassSetter;
  set(func: Function): ClassSetter;
  static(func: Function): StaticMethod;
  static_method(func: Function): StaticMethod;
}

const Class = function Class(methods: unknown[]): ClassFunc {
  let construct: ClassFunc | undefined = undefined;
  let parent: ClassFunc | undefined = undefined;

  if (arguments.length > 1) {
    //a parent was passed in

    parent = methods as unknown as ClassFunc;
    methods = arguments[1];
  }

  for (let i = 0; i < methods.length; i++) {
    const f = methods[i] as Function;

    if (f.name == "constructor") {
      construct = f as unknown as ClassFunc;
      break;
    }
  }

  if (construct == undefined) {
    console.trace("Warning, constructor was not defined", methods);

    if (parent != undefined) {
      const p = parent;
      construct = function (this: unknown) {
        (p as unknown as Function).apply(this, arguments);
      } as unknown as ClassFunc;
    } else {
      construct = function () {} as unknown as ClassFunc;
    }
  }

  if (parent != undefined) {
    construct.prototype = Object.create(parent.prototype);
  }

  construct.prototype.__prototypeid__ = prototype_idgen++;
  construct[Symbol.keystr] = function (this: ClassFunc) {
    return this.prototype.__prototypeid__;
  };

  construct.__parent__ = parent;
  construct.__statics__ = [];

  const getters: Record<string, Function> = {};
  const setters: Record<string, Function> = {};
  const getset: Record<string, number> = {};
  const statics: Record<string, Function> = {};

  //handle getters/setters
  for (let i = 0; i < methods.length; i++) {
    const f = methods[i];

    if (f instanceof ClassSetter) {
      setters[f.func.name] = f.func;
      getset[f.func.name] = 1;
    } else if (f instanceof ClassGetter) {
      getters[f.func.name] = f.func;
      getset[f.func.name] = 1;
    } else if (f instanceof StaticMethod) {
      statics[f.func.name] = f.func;
    }
  }

  for (const k in statics) {
    construct[k] = statics[k];
  }

  for (const k in getset) {
    const def = {
      enumerable: false,
      configurable: true,
      get: getters[k],
      set: setters[k],
    };

    Object.defineProperty(construct.prototype, k, def as PropertyDescriptor);
  }

  handle_statics(construct, parent);

  if (parent != undefined) construct.__parent__ = parent;

  for (let i = 0; i < methods.length; i++) {
    const f = methods[i];

    if (f instanceof ClassGetter || f instanceof ClassSetter) continue;

    construct.prototype[(f as Function).name] = f;
  }

  return construct;
} as ClassFactory;

Class.getter = Class.get = function (func: Function) {
  return new ClassGetter(func);
};
Class.setter = Class.set = function (func: Function) {
  return new ClassSetter(func);
};

const static_method = function static_method(func: Function) {
  (func as unknown as { _is_static_method: boolean })._is_static_method = true;
  return new StaticMethod(func);
};

Class.static = Class.static_method = function (func: Function) {
  (func as unknown as { _is_static_method: boolean })._is_static_method = true;
  return new StaticMethod(func);
};

const mixin = function mixin(cls: ClassFunc, parent: ClassFunc) {
  for (const k in parent.prototype) {
    if (!(k in cls.prototype)) {
      cls.prototype[k] = parent.prototype[k];
    }
  }
};

// Stamp a native ES6 class with the same identity Class() used to assign, so
// converted classes keep working with the Symbol.keystr-keyed set/hashtable
// collections (idempotent — safe to call once per converted class).
function registerClass(cls: Function) {
  const c = cls as unknown as ClassFunc;
  c.prototype.__prototypeid__ = prototype_idgen++;
  c[Symbol.keystr] = function (this: ClassFunc) {
    return this.prototype.__prototypeid__;
  };
  return cls;
}

export { defined_classes, init_prototype, inherit, Class, static_method, mixin, registerClass };
